import { useState, useEffect } from 'react'
import { Card, CardHeader, CardBody, Button, Label, Input, FormFeedback, Alert, Form, FormGroup, Row, Col } from 'reactstrap'
import useForm from '../CustomHooks/FormHook'
import { EXPORT_SCHEMA_MUTATION, IMPORT_SCHEMA_MUTATION, FINISH_COPY_MUTATION } from '../../graphql'
import { handleGraphQLErrors, dateTimeStamp, publishEvent, configValue } from '../../utils.js'
import { gql } from '@apollo/client'
import PubSub from 'pubsub-js'
import SchemaSelect from './SchemaSelect'
import DatapumpJobs from './DatapumpJobs'
import ExportHistory from './DatapumpHistory'
import { FaArrowRight } from 'react-icons/fa'

const EXPORT_SCHEMA = gql(EXPORT_SCHEMA_MUTATION)
const IMPORT_SCHEMA = gql(IMPORT_SCHEMA_MUTATION)
const FINISH_COPY = gql(FINISH_COPY_MUTATION)

export default function CopySchema() {
  console.log('CopySchema rendered')

  const source = 'COPY_SCHEMA'
  const directory = configValue('DEFAULT ORACLE DIRECTORY')
  const { inputs, handleInputChange } = useForm({ sourceSchemaName: 'ASBESTOS', destinationSchemaName: '' }, handleSubmit)
  const [excludeFiles, setExcludeFiles] = useState(true)
  const [sourceSchemaNameState, setSourceSchemaNameState] = useState({ state: '', message: '' })
  const [destinationSchemaNameState, setDestinationSchemaNameState] = useState({ state: '', message: '' })
  const [progress, setProgress] = useState()
  const [graphQLErrors, setGraphQLErrors] = useState([])

  useEffect(() => {
    async function handleExportJobFinishedEvent(msg, data) {
      console.log(`EVENT RECEIVED (CopySchema): ${msg}`, data)
      await importDumpFile()
    }
    async function handleImportJobFinishedEvent(msg, data) {
      console.log(`EVENT RECEIVED (CopySchema): ${msg}`, data)
      await finishCopy()
    }
    const exportToken = PubSub.subscribe(`${source}_EXPORT_JOB_FINISHED`, handleExportJobFinishedEvent)
    const importToken = PubSub.subscribe(`${source}_IMPORT_JOB_FINISHED`, handleImportJobFinishedEvent)
    return function cleanup() {
      PubSub.unsubscribe(exportToken)
      PubSub.unsubscribe(importToken)
    }
  })

  async function finishCopy() {
    console.log('CopySchema.finishCopy: setting password and adding schema...')
    setProgress(`Adding new schema ${inputs.destinationSchemaName.toUpperCase()} to the connection pool...`)
    try {
      const variables = { schemaName: inputs.destinationSchemaName }
      const { data, errors } = await window.apolloClient.mutate({ mutation: FINISH_COPY, variables: variables })
      console.log('finishCopy returned: ', data, errors)
      if (errors) {
        setGraphQLErrors(errors)
      }
    } catch (err) {
      handleGraphQLErrors(err, setGraphQLErrors)
    } finally {
      setProgress(`Schema ${inputs.sourceSchemaName.toUpperCase()} was successfully copied to ${inputs.destinationSchemaName.toUpperCase()}.`)
    }
  }

  async function importDumpFile() {
    console.log('CopySchema.importDumpFile: importing dump file...')
    const fileName = `copy_${inputs.sourceSchemaName.toLowerCase()}_to_${inputs.destinationSchemaName.toLowerCase()}.dmp`
    setProgress(`Importing dump file ${fileName} to ${inputs.destinationSchemaName.toUpperCase()}...`)
    const options = {
      jobName: `IMPORT:${inputs.sourceSchemaName}`,
      oldSchemaName: inputs.sourceSchemaName,
      newSchemaName: inputs.destinationSchemaName.toUpperCase()
    }
    try {
      const variables = { fileName: fileName, directoryName: directory, options: options }
      const { data, errors } = await window.apolloClient.mutate({ mutation: IMPORT_SCHEMA, variables: variables })
      console.log('ImportSchema schema returned: ', data, errors)
      if (data.importSchema) {
        const eventData = {
          jobName: data.importSchema,
          operation: 'IMPORT',
          started: dateTimeStamp(),
          schema: inputs.destinationSchemaName,
          directory: directory,
          status: 'SUBMITTED',
          finish: ''
        }
        publishEvent(`${source}_DATAPUMP_JOB_SUBMITTED`, eventData, 'CopySchema')
      } 
      if (errors) {
        setGraphQLErrors(errors)
      }
    } catch (err) {
      handleGraphQLErrors(err, setGraphQLErrors)
    }
  }

  async function exportSchema() {
    console.log('CopySchema.exportSchema: exporting schema...')
    setProgress(`Exporting schema ${inputs.sourceSchemaName.toUpperCase()}...`)
    const options = {
      jobName: `EXPORT:${inputs.sourceSchemaName}`,
      fileName: `copy_${inputs.sourceSchemaName.toLowerCase()}_to_${inputs.destinationSchemaName.toLowerCase()}.dmp`
    }
    if (excludeFiles) {
      options.excludeFiles = true
    }
    try {
      const variables = { schemaName: inputs.sourceSchemaName, directoryName: directory, options: options }
      const { data, errors } = await window.apolloClient.mutate({ mutation: EXPORT_SCHEMA, variables: variables })
      console.log('export schema returned: ', data, errors)
      if (data.exportSchema) {
        const eventData = {
          jobName: data.exportSchema,
          operation: 'EXPORT',
          started: dateTimeStamp(),
          schema: inputs.sourceSchemaName,
          directory: directory,
          status: 'SUBMITTED',
          finish: ''
        }
        publishEvent(`${source}_DATAPUMP_JOB_SUBMITTED`, eventData, 'CopySchema')
      } 
      if (errors) {
        setGraphQLErrors(errors)
      }
    } catch (err) {
      handleGraphQLErrors(err, setGraphQLErrors)
    }
  }

  async function handleSubmit() {
    console.log('CopySchema.handleSubmit: ', inputs, { excludeFiles })
    setGraphQLErrors([])
    let state = null
    state = inputs.sourceSchemaName ? { state: '', message: '' } : { state: 'has-danger', message: 'Source Schema is required.' }
    setSourceSchemaNameState(state)
    state = inputs.destinationSchemaName ? { state: '', message: '' } : { state: 'has-danger', message: 'Destination Schema Name is required.' }
    setDestinationSchemaNameState(state)

    if (inputs.sourceSchemaName && inputs.destinationSchemaName) {
      setSourceSchemaNameState({ state: '', message: '' })
      setDestinationSchemaNameState({ state: '', message: '' })
      exportSchema()
    }
  }

  let button =  <Button color="primary" onClick={handleSubmit}>Copy Schema</Button>
  if (progress) {
    button = ''
  }

  let newSchemaLink = ''
  let infoColor = 'info'
  if (progress && progress.includes('successfully copied')) {
    infoColor = 'success'
    newSchemaLink = 
    <div style={{ textAlign: 'center', paddingTop: '10px' }}>
      <Button color="success" tag="a" target="_blank" href={`${window.location.origin}/${inputs.destinationSchemaName.toLowerCase()}/login`}>{`Log into schema ${inputs.destinationSchemaName.toUpperCase()}`} <FaArrowRight /></Button>
    </div>
  }

  let cardBody = 
    <CardBody>
      <Alert color={infoColor} isOpen={progress ? true : false}>{progress}</Alert>
      <Alert color="danger" isOpen={graphQLErrors.length ? true : false}>{graphQLErrors.length ? graphQLErrors[0].message : ''}</Alert>
      <Form>
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="sourceSchemaName">Source Schema</Label>
              <SchemaSelect
                defaultValue={inputs.sourceSchemaName}
                inputName={'sourceSchemaName'}
                handleChange={handleInputChange} 
                invalid={sourceSchemaNameState.state === 'has-danger'}
                valid={sourceSchemaNameState.state === 'has-success'}
              />
              <FormFeedback>{sourceSchemaNameState.message}</FormFeedback>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="destinationSchemaName">Destination Schema Name</Label>
              <Input
                id="destinationSchemaName"
                name="destinationSchemaName"
                invalid={destinationSchemaNameState.state === 'has-danger'}
                valid={destinationSchemaNameState.state === 'has-success'}
                onChange={handleInputChange}
                data-cy="destination-schema-name"
              />
              <FormFeedback>{destinationSchemaNameState.message}</FormFeedback>
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col>
            <FormGroup switch>
              <Input 
                name={'excludeFiles'}
                checked={excludeFiles}
                onChange={() => { setExcludeFiles(!excludeFiles) }}
                type="switch"
                role="switch"
              />
              <Label check>Exclude files</Label>
            </FormGroup>
          </Col>
        </Row>
      </Form>
      <div style={{ paddingTop: '10px', textAlign: 'center' }}>
        {button}
      </div>
      <ExportHistory source={source} />
      <DatapumpJobs source={source} />
      {newSchemaLink}
    </CardBody>

  return (
    <Card style={{ maxWidth: '700px', marginTop: '20px' }}>
      <CardHeader style={{ fontWeight: 'bold' }}>Copy Schema</CardHeader>
      {cardBody}
    </Card>
  )
}
